/** Tipos y helpers — solicitudes de ampliación/renovación post-licencia. */
import { moduleLabel, resolveCommercialModuleCatalog } from "@/lib/commercialModuleCatalog";
import { isEntitlementActive } from "@/modules/catalogModulos";
import { buildLicenseRequestPayload } from "@/lib/commercialLicenseRequests";
import { allMainModulesActive } from "@/lib/hubModuleCommercialState";
import { commercialPlanLabel } from "@/lib/commercialPlanUi";
import { LICENSE_TYPE_ANNUAL, LICENSE_TYPE_MONTHLY } from "@/lib/licenseTypes";

export const EXPANSION_KIND_USER_LIMIT = "user_limit_increase";
export const EXPANSION_KIND_CLIENT_LIMIT = "client_limit_increase";
export const EXPANSION_KIND_RENEWAL = "renewal";
export const EXPANSION_KIND_MODULE = "module_upgrade";
export const EXPANSION_KIND_MIXED = "mixed_update";
export const EXPANSION_KIND_SPECIAL = "special_adjustment";

export const EXPANSION_TYPE_OPTIONS = [
  { value: EXPANSION_KIND_USER_LIMIT, label: "Aumentar auxiliares / usuarios" },
  { value: EXPANSION_KIND_CLIENT_LIMIT, label: "Aumentar clientes / empresas" },
  { value: EXPANSION_KIND_RENEWAL, label: "Renovar vigencia" },
  { value: EXPANSION_KIND_MODULE, label: "Agregar módulos" },
  { value: EXPANSION_KIND_MIXED, label: "Ampliación mixta" },
  { value: EXPANSION_KIND_SPECIAL, label: "Ajuste especial / comentario" },
];

export const EXPANSION_KIND_LABELS = Object.fromEntries(
  EXPANSION_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);

export const EXPANSION_KIND_PLAN_UPGRADE = "plan_upgrade";

export const BILLING_CADENCE_OPTIONS = [
  { id: "monthly", label: "Mensual" },
  { id: "annual", label: "Anual" },
];

/** @param {string} [cadence] */
export function licenseTypeFromBillingCadence(cadence) {
  return String(cadence || "").trim().toLowerCase() === "monthly"
    ? LICENSE_TYPE_MONTHLY
    : LICENSE_TYPE_ANNUAL;
}

/** @param {object} [systemInfo] */
export function readCommercialPlanFromSystemInfo(systemInfo = {}) {
  const lic = systemInfo?.license || {};
  const sub = systemInfo?.organization_subscription || {};
  const plan = String(
    lic.commercial_plan || sub.commercial_plan || "",
  ).trim().toLowerCase();
  const cadence = String(
    lic.billing_cadence || sub.billing_cadence || "annual",
  ).trim().toLowerCase();
  if (!["basic", "professional"].includes(plan)) {
    return { hasPlan: false, plan: "", cadence: "annual", label: "" };
  }
  return {
    hasPlan: true,
    plan,
    cadence: cadence === "monthly" ? "monthly" : "annual",
    label: commercialPlanLabel(plan),
  };
}

/** Opciones visibles según licencia activa y modelo por planes. */
export function expansionTypeOptionsForContext(systemInfo = {}) {
  const planCtx = readCommercialPlanFromSystemInfo(systemInfo);
  let options = [...EXPANSION_TYPE_OPTIONS];
  if (!planCtx.hasPlan) return options;

  options = options.filter(
    (o) => o.value !== EXPANSION_KIND_MIXED && o.value !== EXPANSION_KIND_SPECIAL,
  );
  if (planCtx.plan === "professional" || allMainModulesActive(systemInfo)) {
    options = options.filter((o) => o.value !== EXPANSION_KIND_MODULE);
  }
  if (planCtx.plan === "basic") {
    options.push({
      value: EXPANSION_KIND_PLAN_UPGRADE,
      label: "Cambiar a plan Profesional",
    });
  }
  return options;
}

/** @param {object} [systemInfo] */
export function readLicenseExpansionContext(systemInfo = {}) {
  const lic = systemInfo?.license || {};
  const payload = lic?.payload || {};
  const catalog = resolveCommercialModuleCatalog(systemInfo);
  const activeIds = catalog
    .filter((m) => {
      const ctx = { entitlements: systemInfo?.entitlements, modulePermissions: systemInfo?.module_permissions };
      return isEntitlementActive(m.id, ctx);
    })
    .map((m) => m.id);
  const allIds = catalog.map((m) => m.id);
  const currentModules = {};
  allIds.forEach((id) => {
    currentModules[id] = activeIds.includes(id);
  });
  const planCtx = readCommercialPlanFromSystemInfo(systemInfo);
  return {
    titular:
      (systemInfo?.profile?.razon_social || "").trim()
      || (payload.licensee_name || "").trim()
      || "—",
    rfc: (systemInfo?.profile?.rfc || payload.licensee_rfc || "").trim().toUpperCase(),
    commercialPlan: planCtx.hasPlan ? planCtx.plan : null,
    commercialPlanLabel: planCtx.hasPlan ? planCtx.label : null,
    billingCadence: planCtx.hasPlan ? planCtx.cadence : null,
    usesPlanModel: planCtx.hasPlan,
    authorizedUsers: lic?.authorized_users ?? payload?.authorized_users ?? null,
    authorizedClients: lic?.authorized_clients ?? payload?.authorized_clients ?? null,
    activeUsers: lic?.active_users ?? null,
    activeClients: lic?.active_clients ?? null,
    availableUsers: lic?.available_users ?? null,
    availableClients: lic?.available_clients ?? null,
    validFrom: String(payload.valid_from || "").slice(0, 10) || null,
    validUntil: String(payload.valid_to || "").slice(0, 10) || null,
    currentModules,
    catalog,
    availableModuleIds: allIds.filter((id) => !currentModules[id]),
  };
}

/** @param {object} ctx @param {object} form */
export function buildWhatsAppExpansionMessage(ctx, form) {
  const razon = ctx.titular || "mi organización";
  const rfc = ctx.rfc || "—";
  const comments = String(form.comments || "").trim() || "______";
  const kind = form.expansion_type || EXPANSION_KIND_SPECIAL;

  if (kind === EXPANSION_KIND_USER_LIMIT) {
    return (
      `Hola BNT, solicito ampliar mi licencia de ${razon}, RFC ${rfc}. ` +
      `Actualmente tengo ${ctx.authorizedUsers ?? "—"} usuarios autorizados y solicito aumentar a ${form.requested_authorized_users ?? "—"}. ` +
      `Comentarios: ${comments}.`
    );
  }
  if (kind === EXPANSION_KIND_CLIENT_LIMIT) {
    return (
      `Hola BNT, solicito ampliar mi licencia de ${razon}, RFC ${rfc}. ` +
      `Actualmente tengo ${ctx.authorizedClients ?? "—"} clientes autorizados y solicito aumentar a ${form.requested_authorized_clients ?? "—"}. ` +
      `Comentarios: ${comments}.`
    );
  }
  if (kind === EXPANSION_KIND_MODULE) {
    const mods = Object.entries(form.requested_modules || {})
      .filter(([, on]) => on)
      .map(([id]) => moduleLabel(id, ctx.catalog))
      .join(", ");
    return (
      `Hola BNT, solicito agregar módulos a mi licencia de ${razon}, RFC ${rfc}. ` +
      `Módulos solicitados: ${mods || "—"}. Comentarios: ${comments}.`
    );
  }
  return (
    `Hola BNT, solicito ampliación/renovación de licencia de ${razon}, RFC ${rfc}. ` +
    `Tipo: ${EXPANSION_KIND_LABELS[kind] || kind}. Comentarios: ${comments}.`
  );
}

/** @param {object} form @param {string[]} requestableIds */
export function expansionModulesForLegalCheck(form = {}, requestableIds = []) {
  const mods = {};
  requestableIds.forEach((id) => {
    if (form.requested_modules?.[id]) mods[id] = true;
  });
  return mods;
}

/** @param {object} form @param {object} user @param {string} agreementAcceptanceId @param {object} [opts] */
export function buildExpansionRequestPayload(form, user, agreementAcceptanceId, opts = {}) {
  const kind = form.expansion_type || EXPANSION_KIND_SPECIAL;
  const requestedModules = { ...(form.requested_modules || {}) };
  const planUpgrade = kind === EXPANSION_KIND_PLAN_UPGRADE;
  const renewalForm = {
    ...form,
    request_kind: planUpgrade ? EXPANSION_KIND_RENEWAL : kind,
    requested_modules: requestedModules,
    requested_authorized_users: form.requested_authorized_users
      ? Number(form.requested_authorized_users)
      : undefined,
    requested_authorized_clients: form.requested_authorized_clients
      ? Number(form.requested_authorized_clients)
      : undefined,
  };
  if (planUpgrade) {
    renewalForm.commercial_plan = "professional";
    renewalForm.billing_cadence = form.billing_cadence || "annual";
    renewalForm.requested_license_type = licenseTypeFromBillingCadence(renewalForm.billing_cadence);
  } else if (kind === EXPANSION_KIND_RENEWAL && form.commercial_plan) {
    renewalForm.billing_cadence = form.billing_cadence || "annual";
    renewalForm.requested_license_type = licenseTypeFromBillingCadence(renewalForm.billing_cadence);
  }
  const payload = buildLicenseRequestPayload(
    renewalForm,
    user,
    agreementAcceptanceId,
    { hasActiveLicense: true, requestableModuleIds: opts.requestableModuleIds || [] },
  );
  if (form.requested_authorized_users) {
    payload.requested_authorized_users = Number(form.requested_authorized_users);
  }
  if (form.requested_authorized_clients) {
    payload.requested_authorized_clients = Number(form.requested_authorized_clients);
  }
  payload.request_kind = planUpgrade ? EXPANSION_KIND_PLAN_UPGRADE : kind;
  return payload;
}
